import enumDescription from "../../services/enumDescription";

// Cadastro de fluxos (#480058): o modelo que o processo do cliente executa.
// A arvore inteira vai e volta da API num JSON so (FluxoEstruturaService);
// etapa e item apontam para papel, modulo e grupo pelo codigo.

export enum TipoPapel {
    INTERNO = 1,
    CLIENTE = 2,
}

export enum RegraInicio {
    APOS_ANTERIOR = 1,
    JUNTO_ANTERIOR = 2,
}

export enum TipoItem {
    CHECK = 1,
    SELECAO = 2,
    SELECAO_MULTIPLA = 3,
    TEXTO = 4,
    ARQUIVO = 5,
    DATA = 6,
}

export enum RegraObservacao {
    NAO = 0,
    OPCIONAL = 1,
    OBRIGATORIA = 2,
    CONFORME_OPCAO = 3,
}

export const TIPOS_SELECAO = [TipoItem.SELECAO, TipoItem.SELECAO_MULTIPLA];

export const TipoPapelList = new enumDescription([
    {value: TipoPapel.INTERNO, description: 'Interno'},
    {value: TipoPapel.CLIENTE, description: 'Cliente'},
]);

export const RegraInicioList = new enumDescription([
    {value: RegraInicio.APOS_ANTERIOR, description: 'Após a anterior'},
    {value: RegraInicio.JUNTO_ANTERIOR, description: 'Junto com a anterior'},
]);

export const TipoItemList = new enumDescription([
    {value: TipoItem.CHECK, description: 'Check'},
    {value: TipoItem.SELECAO, description: 'Seleção única'},
    {value: TipoItem.SELECAO_MULTIPLA, description: 'Seleção múltipla'},
    {value: TipoItem.TEXTO, description: 'Texto'},
    {value: TipoItem.ARQUIVO, description: 'Arquivo'},
    {value: TipoItem.DATA, description: 'Data'},
]);

export const RegraObservacaoList = new enumDescription([
    {value: RegraObservacao.NAO, description: 'Não pede'},
    {value: RegraObservacao.OPCIONAL, description: 'Opcional'},
    {value: RegraObservacao.OBRIGATORIA, description: 'Obrigatória'},
    {value: RegraObservacao.CONFORME_OPCAO, description: 'Obrigatória conforme a opção'},
]);

export interface Referencia {
    id: number,
    nome: string,
}

export interface Papel {
    id?: number,
    codigo: string,
    nome: string,
    tipo: TipoPapel,
    setor: Referencia | null,
    usuario: Referencia | null,
}

export interface Modulo {
    id?: number,
    codigo: string,
    nome: string,
    opcional: boolean,
    /** Icone do cartao no portal do cliente (#484909); null = pelo nome do modulo. */
    icone?: string | null,
}

export interface Grupo {
    id?: number,
    codigo: string,
    nome: string,
}

export interface Opcao {
    valor?: string,
    rotulo: string,
    exige_observacao: boolean,
}

export interface Item {
    id?: number,
    /** Chave so da tela (arrastar e selecionar); nao vai para a API. */
    _k: string,
    grupo: string | null,
    modulo: string | null,
    papel: string,
    titulo: string,
    orientacao: string | null,
    tipo: TipoItem,
    opcoes: Opcao[],
    observacao: RegraObservacao,
    exige_anexo: boolean,
    permite_declinar: boolean,
    prazo_dias_uteis: number,
    visivel_cliente: boolean,
    acao_cliente: string | null,
    ativo: boolean,
}

export interface Etapa {
    id?: number,
    _k: string,
    nome: string,
    descricao: string | null,
    duracao_dias_uteis: number,
    regra_inicio: RegraInicio,
    papel: string,
    visivel_cliente: boolean,
    ativo: boolean,
    grupos: Grupo[],
    itens: Item[],
}

export interface Fluxo {
    id?: number,
    codigo: string,
    nome: string,
    descricao: string | null,
    ativo: boolean,
    versao?: number,
    data_alteracao?: string | null,
    usuario_alteracao?: string | null,
    /** Setores do fluxo (#484909): so os usuarios deles ocupam os papeis. Vazio = todos. */
    setores: Referencia[],
    papeis: Papel[],
    modulos: Modulo[],
    etapas: Etapa[],
}

export interface Previa {
    inicio: string,
    fim: string,
    dias_uteis: number,
    aviso: string | null,
    etapas: Array<{
        indice: number,
        nome: string,
        inicio: string,
        fim: string,
        itens: Array<{indice: number, titulo: string, prazo: string}>,
    }>,
}

let sequencia = 0;
export const novaChave = () => `k${Date.now().toString(36)}${(sequencia++).toString(36)}`;

export const novoItem = (papel: string, grupo: string | null): Item => ({
    _k: novaChave(),
    grupo,
    modulo: null,
    papel,
    titulo: '',
    orientacao: null,
    tipo: TipoItem.CHECK,
    opcoes: [],
    observacao: RegraObservacao.OPCIONAL,
    exige_anexo: false,
    permite_declinar: true,
    prazo_dias_uteis: 1,
    visivel_cliente: true,
    acao_cliente: null,
    ativo: true,
});

export const novaEtapa = (papel: string): Etapa => ({
    _k: novaChave(),
    nome: '',
    descricao: null,
    duracao_dias_uteis: 5,
    regra_inicio: RegraInicio.APOS_ANTERIOR,
    papel,
    visivel_cliente: true,
    ativo: true,
    grupos: [],
    itens: [],
});

/** Fluxo da API -> estado da tela (chaves de arrastar). */
export const paraTela = (dados: any): Fluxo => ({
    ...dados,
    etapas: (dados.etapas || []).map((etapa: any) => ({
        ...etapa,
        _k: novaChave(),
        itens: (etapa.itens || []).map((item: any) => ({...item, opcoes: item.opcoes || [], _k: novaChave()})),
    })),
});

/** Estado da tela -> corpo da API, sem as chaves da tela. */
export const paraApi = (fluxo: Fluxo) => ({
    ...fluxo,
    etapas: fluxo.etapas.map(({_k, itens, ...etapa}) => ({
        ...etapa,
        itens: itens.map(({_k: chave, ...item}) => item),
    })),
});

export const formataData = (data: string) => {
    const [ano, mes, dia] = data.split('-');
    return `${dia}/${mes}/${ano}`;
};

/** Os itens da etapa seguem a ordem dos grupos: o que esta sem grupo vem primeiro. */
export const ordenaPorGrupo = (etapa: Etapa): Etapa => {
    const ordem = new Map(etapa.grupos.map((grupo, i) => [grupo.codigo, i + 1]));
    const posicao = (item: Item) => (item.grupo !== null && ordem.has(item.grupo) ? ordem.get(item.grupo)! : 0);
    const itens = etapa.itens
        .map((item, i) => ({item, i}))
        .sort((a, b) => posicao(a.item) - posicao(b.item) || a.i - b.i)
        .map(({item}) => item);
    return {...etapa, itens};
};

/** O que esta aberto no painel: a etapa (sem item) ou um item dela. */
export interface Selecao {
    etapa: string,
    item?: string,
}
